<!-- 
  通用图表组件
  支持动态渲染不同类型的图表，包括柱状图、折线图、饼图等
  基于Ant Design Vue的图表组件封装
-->
<template>
  <div class="dynamic-chart-container">
    <a-card :title="title" :bordered="false" :bodyStyle="{ padding: '12px' }">
      <a-spin :spinning="loading">
        <div ref="chartContainer" :style="{ height: `${height}px`, width: '100%' }"></div>
      </a-spin>
    </a-card>
  </div>
</template>

<script lang="ts">
import { defineComponent, ref, onMounted, onBeforeUnmount, watch, PropType } from 'vue';
import * as echarts from 'echarts/core';
import {
  BarChart,
  LineChart,
  PieChart,
  GaugeChart,
} from 'echarts/charts';
import {
  TitleComponent,
  TooltipComponent,
  LegendComponent,
  GridComponent,
  DatasetComponent,
  TransformComponent,
} from 'echarts/components';
import { LabelLayout, UniversalTransition } from 'echarts/features';
import { CanvasRenderer } from 'echarts/renderers';

// 注册必要的组件
echarts.use([
  TitleComponent,
  TooltipComponent,
  LegendComponent,
  GridComponent,
  DatasetComponent,
  TransformComponent,
  BarChart,
  LineChart,
  PieChart,
  GaugeChart,
  LabelLayout,
  UniversalTransition,
  CanvasRenderer
]);

export default defineComponent({
  name: 'ChartComponent',
  props: {
    title: {
      type: String,
      default: ''
    },
    type: {
      type: String,
      required: true,
      validator: (value: string) => {
        return ['bar', 'line', 'pie', 'gauge'].includes(value);
      }
    },
    data: {
      type: Array as PropType<any[]>,
      required: true
    },
    xField: {
      type: String,
      default: ''
    },
    yField: {
      type: String,
      default: ''
    },
    seriesField: {
      type: String,
      default: ''
    },
    valueField: {
      type: String,
      default: ''
    },
    categoryField: {
      type: String,
      default: ''
    },
    height: {
      type: Number,
      default: 300
    },
    colors: {
      type: Array as PropType<string[]>,
      default: () => []
    },
    ranges: {
      type: Array as PropType<{ value: number, color: string }[]>,
      default: () => []
    }
  },
  setup(props) {
    const chartContainer = ref<HTMLElement | null>(null);
    const chartInstance = ref<echarts.ECharts | null>(null);
    const loading = ref(false);

    // 初始化图表
    const initChart = () => {
      if (!chartContainer.value) return;
      
      // 如果已经有实例，先销毁
      if (chartInstance.value) {
        chartInstance.value.dispose();
      }
      
      // 创建新实例
      chartInstance.value = echarts.init(chartContainer.value);
      
      // 更新图表数据
      updateChart();
    };

    // 更新图表数据
    const updateChart = () => {
      if (!chartInstance.value) return;
      
      loading.value = true;
      
      let option: any = {};
      
      // 根据类型创建不同的图表
      switch (props.type) {
        case 'bar':
          option = createBarChartOption();
          break;
        case 'line':
          option = createLineChartOption();
          break;
        case 'pie':
          option = createPieChartOption();
          break;
        case 'gauge':
          option = createGaugeChartOption();
          break;
      }
      
      // 设置通用配置
      chartInstance.value.setOption(option);
      loading.value = false;
    };
    
    // 创建柱状图配置
    const createBarChartOption = () => {
      // 处理数据分组
      let seriesData: any[] = [];
      
      if (props.seriesField) {
        // 分组柱状图
        const groupBy = (array: any[], key: string) => {
          return array.reduce((result: any, currentValue: any) => {
            (result[currentValue[key]] = result[currentValue[key]] || []).push(currentValue);
            return result;
          }, {});
        };
        
        const grouped = groupBy(props.data, props.seriesField);
        
        seriesData = Object.keys(grouped).map(key => {
          return {
            name: key,
            type: 'bar',
            data: grouped[key].map((item: any) => item[props.yField])
          };
        });
      } else {
        // 普通柱状图
        seriesData = [{
          type: 'bar',
          data: props.data.map(item => item[props.yField])
        }];
      }
      
      return {
        tooltip: {
          trigger: 'axis',
          axisPointer: {
            type: 'shadow'
          }
        },
        legend: props.seriesField ? { orient: 'horizontal', bottom: 0 } : undefined,
        xAxis: {
          type: 'category',
          data: props.data.map(item => item[props.xField]),
          axisLabel: {
            interval: 0,
            rotate: props.data.length > 10 ? 45 : 0
          }
        },
        yAxis: {
          type: 'value'
        },
        series: seriesData,
        color: props.colors.length > 0 ? props.colors : undefined
      };
    };
    
    // 创建折线图配置
    const createLineChartOption = () => {
      // 处理数据分组
      let seriesData: any[] = [];
      
      if (props.seriesField) {
        // 分组折线图
        const groupBy = (array: any[], key: string) => {
          return array.reduce((result: any, currentValue: any) => {
            (result[currentValue[key]] = result[currentValue[key]] || []).push(currentValue);
            return result;
          }, {});
        };
        
        const grouped = groupBy(props.data, props.seriesField);
        
        seriesData = Object.keys(grouped).map(key => {
          return {
            name: key,
            type: 'line',
            data: grouped[key].map((item: any) => item[props.yField])
          };
        });
      } else {
        // 普通折线图
        seriesData = [{
          type: 'line',
          data: props.data.map(item => item[props.yField])
        }];
      }
      
      return {
        tooltip: {
          trigger: 'axis'
        },
        legend: props.seriesField ? { orient: 'horizontal', bottom: 0 } : undefined,
        xAxis: {
          type: 'category',
          data: props.data.map(item => item[props.xField]),
          axisLabel: {
            interval: 0,
            rotate: props.data.length > 10 ? 45 : 0
          }
        },
        yAxis: {
          type: 'value'
        },
        series: seriesData,
        color: props.colors.length > 0 ? props.colors : undefined
      };
    };
    
    // 创建饼图配置
    const createPieChartOption = () => {
      return {
        tooltip: {
          trigger: 'item',
          formatter: '{a} <br/>{b}: {c} ({d}%)'
        },
        legend: {
          orient: 'horizontal',
          bottom: 0
        },
        series: [
          {
            name: props.title || '数据分布',
            type: 'pie',
            radius: ['40%', '70%'],
            avoidLabelOverlap: false,
            itemStyle: {
              borderRadius: 10,
              borderColor: '#fff',
              borderWidth: 2
            },
            label: {
              show: false,
              position: 'center'
            },
            emphasis: {
              label: {
                show: true,
                fontSize: '14',
                fontWeight: 'bold'
              }
            },
            labelLine: {
              show: false
            },
            data: props.data.map(item => ({
              name: item[props.categoryField],
              value: item[props.valueField]
            }))
          }
        ],
        color: props.colors.length > 0 ? props.colors : undefined
      };
    };
    
    // 创建仪表盘配置
    const createGaugeChartOption = () => {
      const value = props.data[0]?.[props.valueField] || 0;
      
      // 计算颜色区间
      let axisLine = {
        lineStyle: {
          width: 30,
          color: [
            [0.3, '#ff6e76'],
            [0.7, '#fddd60'],
            [1, '#58d9f9']
          ]
        }
      };
      
      // 如果提供了自定义区间
      if (props.ranges && props.ranges.length > 0) {
        const sortedRanges = [...props.ranges].sort((a, b) => a.value - b.value);
        axisLine.lineStyle.color = sortedRanges.map(range => [range.value, range.color]);
      }
      
      return {
        series: [
          {
            type: 'gauge',
            startAngle: 180,
            endAngle: 0,
            min: 0,
            max: 1,
            splitNumber: 10,
            axisLine,
            pointer: {
              icon: 'path://M12.8,0.7l12,40.1H0.7L12.8,0.7z',
              length: '12%',
              width: 12,
              offsetCenter: [0, '-60%'],
              itemStyle: {
                color: 'auto'
              }
            },
            axisTick: {
              length: 12,
              lineStyle: {
                color: 'auto',
                width: 2
              }
            },
            splitLine: {
              length: 20,
              lineStyle: {
                color: 'auto',
                width: 5
              }
            },
            axisLabel: {
              color: '#464646',
              fontSize: 14,
              distance: -60,
              formatter: function (value: number) {
                if (value === 0.5) {
                  return '50%';
                }
                if (value === 1) {
                  return '100%';
                }
                return '';
              }
            },
            title: {
              offsetCenter: [0, '-20%'],
              fontSize: 14
            },
            detail: {
              fontSize: 20,
              offsetCenter: [0, '0%'],
              valueAnimation: true,
              formatter: function (value: number) {
                return Math.round(value * 100) + '%';
              },
              color: 'auto'
            },
            data: [{
              value,
              name: props.title
            }]
          }
        ]
      };
    };
    
    // 监听窗口大小变化
    const handleResize = () => {
      if (chartInstance.value) {
        chartInstance.value.resize();
      }
    };
    
    // 监听数据变化
    watch(() => props.data, () => {
      updateChart();
    }, { deep: true });
    
    watch([() => props.type, () => props.xField, () => props.yField, 
           () => props.seriesField, () => props.valueField, () => props.categoryField], () => {
      updateChart();
    });
    
    onMounted(() => {
      initChart();
      window.addEventListener('resize', handleResize);
    });
    
    onBeforeUnmount(() => {
      if (chartInstance.value) {
        chartInstance.value.dispose();
        chartInstance.value = null;
      }
      window.removeEventListener('resize', handleResize);
    });
    
    return {
      chartContainer,
      loading
    };
  }
});
</script>

<style scoped>
.dynamic-chart-container {
  width: 100%;
  border-radius: 2px;
  margin-bottom: 16px;
}
</style> 